import { conditionRadar, filledAxisCount } from './fatigueRadar';
import { METRIC_TAB_IDS, hasMetric } from './resultCopy';

import type { DiagnosisRecord } from './record';
import type { MetricTabId } from './resultCopy';

/**
 * 이 회차가 실제로 그릴 것이 있는 탭들 — **빈 자리 정책의 정본**이다.
 *
 * 결과 본문(`ResultReport`)·결과 상세(`ResultDetailPage`)·홈 타일(`homeTiles`)이
 * 각자 판단하면 같은 회차를 세 화면이 다르게 말한다(홈은 값을 그리는데 탭은 비어 있는
 * 상태가 실제로 났다). 판정을 한 자리에 두고 셋이 같이 읽는다.
 *
 * **값이 하나도 없는 탭은 안 세운다.** 예전에는 일곱을 다 세우고 값 자리에
 * `측정 준비 중`을 그렸는데, 그 문구는 App Store Review Guideline 2.1(App Completeness)이
 * 말하는 미완 표식으로 읽힌다. 축이 있다는 사실은 빈 탭이 아니라 다음 회차의 값으로
 * 알린다 — 눌러도 아무 일이 없는 것을 그리느니 자리를 비운다(`ResultDetailPage`의
 * 공유 아이콘과 같은 규칙).
 *
 * 일부만 결측인 탭은 **선다** — 그 칸만 빈 상태 문구다. 탭을 통째로 감추는 것은
 * 그릴 값이 하나도 없을 때뿐이다.
 *
 * **피로도만 이력을 본다.** 그 축들은 이 회차가 아니라 컨디션별 이력에서 나오기
 * 때문이다(`conditionRadar`). `history`를 안 주면 피로도는 안 선다 — 진단 직후의 결과
 * 본문처럼 회차 하나만 들고 있는 자리가 그렇다.
 *
 * React·저장소 IO 의존 없음 — vitest 대상.
 */
export const visibleTabs = (
  record: DiagnosisRecord,
  history: readonly DiagnosisRecord[] = [],
): MetricTabId[] =>
  METRIC_TAB_IDS.filter((id) =>
    id === 'fatigue' ? hasFatigue(history) : hasMetric(record, id),
  );

/**
 * 피로도 탭이 설 조건 — **값 있는 축이 둘 이상**.
 *
 * 레이더는 비교가 본질이고 점 하나는 비교가 아니다. 축 하나짜리 레이더는 격자 위에 점을
 * 찍어 놓고 그것이 무슨 뜻인지는 말하지 못한다 — 같은 컨디션을 한 번 더 재도 아무것도
 * 달라지지 않는다. 둘이 되는 순간 "좋은 날과 나쁜 날이 이만큼 다르다"가 생긴다.
 */
const hasFatigue = (history: readonly DiagnosisRecord[]): boolean =>
  filledAxisCount(conditionRadar(history)) >= 2;

/**
 * `/result/:id/:tab` 이 가리킬 수 있는 탭인가. 주소를 손으로 고쳤거나, 값이 있던 시절의
 * 링크를 나중에 다시 열었을 때 빈 화면 대신 첫 가시 탭으로 보내는 판정이다.
 */
export const isVisibleTab = (
  record: DiagnosisRecord,
  tab: string | undefined,
  history: readonly DiagnosisRecord[] = [],
): boolean => visibleTabs(record, history).some((id) => id === tab);
